import React from 'react';
import styled from 'styled-components';
import {
  Avatar,
  BillableIcon,
  DateTime,
  Hours,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  MemberContactPopover,
  Page,
  Percent,
  PlaceholderAvatar,
  PlaceholderPopover,
} from '~/components';
import { colors } from '~/styles';
import allocationBillableTypes from '~/lookups/allocation-billable-types';
import allocationBookingTypes from '~/lookups/allocation-booking-types';

const Resource = styled.div`
  display: flex;
  align-items: center;
`;

const ResourceName = styled.div`
  margin-left: 0.5rem;
`;

const ProjectInfo = styled.div`
  line-height: 1;

  small {
    display: flex;
    padding-top: 0.25rem;
    font-size: 0.75rem;
    color: ${colors.grey40};
  }
`;

function AllocationHours({ allocation }) {
  switch (allocation.unit) {
    case 'day':
      return (
        <>
          <Hours value={allocation.hoursPerDay} minimumFractionDigits={0} /> / day
        </>
      );
    case 'week':
      return (
        <>
          <Hours value={allocation.hoursPerWeek} minimumFractionDigits={0} /> / week
        </>
      );
    case 'month':
      return (
        <>
          <Hours value={allocation.hoursPerMonth} minimumFractionDigits={0} /> / month
        </>
      );
    case 'allocation':
      return (
        <>
          <Hours value={allocation.hoursPerAllocation} minimumFractionDigits={0} /> total
        </>
      );
    case 'ratio_of_capacity':
      return <Percent value={allocation.hoursRatioOfCapacity} minimumFractionDigits={0} />;
    default:
      return null;
  }
}

export default function AllocationsList({
  data,
  action,
  query,
  columns,
  canManage,
  selection,
  onLoadMore,
  onRowClick,
  onView,
  onEdit,
  onDelete,
  onSort,
}) {
  return (
    <Page.ListView>
      <ListView data-testid="allocations-list">
        <ListView.Header>
          {canManage && (
            <ListViewSelection.Column
              selection={selection}
              disabled={data.results.length === 0 || action === 'filter'}
            />
          )}
          <ListView.Column sticky name="resource" minWidth="13rem" onSort={onSort} sort={query.sort}>
            Resource
          </ListView.Column>
          <ListView.Column name="project" minWidth="18rem" onSort={onSort} sort={query.sort}>
            Assignment
          </ListView.Column>
          <ListView.Column name="role" width="12rem" onSort={onSort} sort={query.sort} isVisible={columns.visible.role}>
            Role
          </ListView.Column>
          <ListView.Column name="task" width="12rem" onSort={onSort} sort={query.sort} isVisible={columns.visible.task}>
            Task
          </ListView.Column>
          <ListView.Column
            name="bookingType"
            width="12rem"
            onSort={onSort}
            sort={query.sort}
            isVisible={columns.visible.bookingType}>
            Booking Type
          </ListView.Column>
          <ListView.Column name="billable" width="12rem" isVisible={columns.visible.billable}>
            Billable Type
          </ListView.Column>
          <ListView.Column
            name="start"
            width="9rem"
            onSort={onSort}
            sort={query.sort}
            isVisible={columns.visible.start}>
            Start
          </ListView.Column>
          <ListView.Column name="end" width="9rem" onSort={onSort} sort={query.sort} isVisible={columns.visible.end}>
            End
          </ListView.Column>
          <ListView.Column name="hours" width="8rem" isVisible={columns.visible.hours}>
            Hours
          </ListView.Column>
          <ListView.Column
            name="totalHours"
            width="12rem"
            align="right"
            onSort={onSort}
            sort={query.sort}
            isVisible={columns.visible.totalHours}>
            Total Allocated
          </ListView.Column>
          <ListViewActions.Column />
        </ListView.Header>

        <ListView.Body fade={action === 'filter'}>
          {data.results.map((allocation) => {
            const {
              id,
              resourceTypeId,
              member,
              placeholder,
              project,
              role,
              task,
              timeOffType,
              bookingTypeId,
              billableTypeId,
              start,
              end,
              totalHours,
            } = allocation;

            return (
              <ListView.Row key={id} onClick={() => onRowClick(allocation)}>
                {canManage && <ListViewSelection.Cell id={id} selection={selection} />}
                <ListView.Cell>
                  {resourceTypeId === 'member' && member && (
                    <Resource>
                      <MemberContactPopover member={member} placement="right">
                        <Avatar value={member} isCircle hasBackground initialsFontSize=".9rem" />
                      </MemberContactPopover>
                      <ResourceName>{member.name}</ResourceName>
                    </Resource>
                  )}
                  {resourceTypeId === 'placeholder' && placeholder && (
                    <Resource>
                      <PlaceholderPopover placeholderId={placeholder.id} placement="right">
                        <PlaceholderAvatar isCircle hasBackground initialsFontSize=".9rem" />
                      </PlaceholderPopover>
                      <ResourceName>{placeholder.name}</ResourceName>
                    </Resource>
                  )}
                </ListView.Cell>
                <ListView.Cell>
                  {project ? (
                    <ProjectInfo>
                      {project.name}
                      {project.client && <small>{project.client.name}</small>}
                    </ProjectInfo>
                  ) : (
                    timeOffType && (
                      <ProjectInfo>
                        {timeOffType.name}
                        <small>Time Off</small>
                      </ProjectInfo>
                    )
                  )}
                </ListView.Cell>
                <ListView.Cell>{role?.name}</ListView.Cell>
                <ListView.Cell>{task?.name}</ListView.Cell>
                <ListView.Cell>{allocationBookingTypes[bookingTypeId]?.name}</ListView.Cell>
                <ListView.Cell>
                  {billableTypeId && (
                    <Resource>
                      <BillableIcon value={billableTypeId === 'billable'} showTooltip={false} />
                      {allocationBillableTypes[billableTypeId]?.name}
                    </Resource>
                  )}
                </ListView.Cell>
                <ListView.Cell>{start && <DateTime value={start} />}</ListView.Cell>
                <ListView.Cell>{end && <DateTime value={end} />}</ListView.Cell>
                <ListView.Cell>
                  <AllocationHours allocation={allocation} />
                </ListView.Cell>
                <ListView.Cell>
                  <Hours value={Number(totalHours)} />
                </ListView.Cell>
                <ListViewActions>
                  {allocation.permissions?.manage ? (
                    <ListViewActions.Edit onClick={() => onEdit(allocation)} />
                  ) : (
                    <ListViewActions.View onClick={() => onView(allocation)} />
                  )}

                  <hr />

                  <ListViewMenu>
                    {({ setIsOpen }) => {
                      const handleAction = (action) => setIsOpen(false) || action;
                      const manage = allocation.permissions?.manage;

                      return (
                        <>
                          <ListViewMenu.Item onClick={() => handleAction(onView(allocation))}>View</ListViewMenu.Item>

                          <ListViewMenu.Item
                            disabled={!manage}
                            tooltip={!manage ? 'Insufficient permissions to edit this allocation.' : undefined}
                            onClick={() => handleAction(onEdit(allocation))}>
                            Edit
                          </ListViewMenu.Item>

                          <ListViewMenu.Item
                            disabled={!manage}
                            tooltip={!manage ? 'Insufficient permissions to delete this allocation.' : undefined}
                            onClick={() => handleAction(onDelete(allocation))}>
                            Delete
                          </ListViewMenu.Item>
                        </>
                      );
                    }}
                  </ListViewMenu>
                </ListViewActions>
              </ListView.Row>
            );
          })}

          {data.results.length === 0 && <ListView.Empty />}

          {data.total > data.results.length && (
            <ListView.Loader key={data.results.length} onIntersecting={onLoadMore} />
          )}
        </ListView.Body>

        <ListView.Status total={data.total} label="Allocation" isLoading={!!action} />
      </ListView>
    </Page.ListView>
  );
}
